import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Container } from "../layout/Container";
import { HeaderSection } from "../layout/CardsContainer";
import bg from "../img/illustrations/background.png";
import useTranslation from "../hooks/useTranslation";
import ENV_CONFIG from "../config/environment-unified";

const Contact = () => {
  const { t } = useTranslation();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        setLoading(true);
        setError(null);

        // We fetch "mentions légales" since it typically contains authoritative contact info.
        const response = await fetch(ENV_CONFIG.LEGAL_MENTIONS_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch contact info: ${response.status}`);
        }

        const data = await response.json();
        const legal = data?.data;
        const nextContact = legal?.contact || null;

        setContact(nextContact);
      } catch (err) {
        setError(err?.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, []);

  return (
    <Page>
      <StyledContainer>
        <HeaderSection title={t("legal.contact", "Contact")} />

        {loading && <InfoText>{t("legal.loadingContent", "Chargement...")}</InfoText>}

        {!loading && error && (
          <ErrorBox>
            <ErrorTitle>{t("legal.errorTitle", "Erreur")}</ErrorTitle>
            <InfoText>{t("legal.errorMessage", "Impossible de charger le contenu.")}</InfoText>
            <ErrorDetails>{error}</ErrorDetails>
          </ErrorBox>
        )}

        {!loading && !error && !contact && (
          <InfoText>
            {t(
              "legal.contentUnavailableMessage",
              "Le contenu demandé n'est pas disponible pour le moment."
            )}
          </InfoText>
        )}

        {!loading && !error && contact && (
          <Card>
            {contact.email && (
              <Row>
                <Label>{t("legal.email", "Email :")}</Label>
                <Value>
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </Value>
              </Row>
            )}
            {contact.phone && (
              <Row>
                <Label>{t("legal.phone", "Téléphone :")}</Label>
                <Value>{contact.phone}</Value>
              </Row>
            )}
            {contact.address && (
              <Row>
                <Label>{t("legal.address", "Adresse :")}</Label>
                <Value>{contact.address}</Value>
              </Row>
            )}
          </Card>
        )}
      </StyledContainer>
    </Page>
  );
};

export default Contact;

const Page = styled.div`
  padding-top: 4rem;
  min-height: 100vh;
  background-image: url(${bg});
  background-size: cover;
  background-attachment: fixed;
`;

const StyledContainer = styled(Container)`
  width: 70%;
  margin: 2rem auto;
  padding: 2rem;
  padding-bottom: 3rem;
  background-color: ${({ theme }) => theme.color.background};
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 95%;
    margin: 1rem auto;
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const InfoText = styled.p`
  margin: 0.75rem 0 0;
  color: ${({ theme }) => theme.color.darkerText};
  line-height: 1.6;
`;

const Card = styled.div`
  margin-top: 1.25rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ theme }) => theme.color.secondaryBg};

  a {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: underline;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.color.border};
  }
`;

const Label = styled.span`
  min-width: 120px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.darkerText};
`;

const Value = styled.span`
  color: ${({ theme }) => theme.color.text};
`;

const ErrorBox = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ theme }) => theme.color.secondaryBg};
`;

const ErrorTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: ${({ theme }) => theme.color.error || "#d32f2f"};
`;

const ErrorDetails = styled.pre`
  margin: 0.75rem 0 0;
  white-space: pre-wrap;
  opacity: 0.75;
`;

